// ─────────────────────────────────────────────────────────────────────────────
// Sistema de 8 direções — vetor de velocidade → frame do spritesheet
//
// É AQUI que "as 8 direções da folha de sprites" são vinculadas ao "vetor de
// velocidade gerado pelo sistema". Leia o README (seção "8 direções") para o
// passo-a-passo; este arquivo é a implementação.
// ─────────────────────────────────────────────────────────────────────────────

import { AnimationConfig, DEFAULT_ROW_ORDER, Dir8, DirectionSet } from './schema';

const TAU = Math.PI * 2;

/**
 * Converte um ângulo de tela (radianos, `atan2(vy, vx)`, y para baixo) no
 * índice de setor 0..7 na ordem E, SE, S, SW, W, NW, N, NE.
 *
 *   índice 0 = E   (→)      índice 4 = W   (←)
 *   índice 1 = SE  (↘)      índice 5 = NW  (↖)
 *   índice 2 = S   (↓)      índice 6 = N   (↑)
 *   índice 3 = SW  (↙)      índice 7 = NE  (↗)
 */
function angleToSector8(angle: number): number {
  // desloca meio setor (22.5°) para o setor "centrar" na direção
  let a = angle + Math.PI / 8;
  a = ((a % TAU) + TAU) % TAU;
  return Math.floor(a / (TAU / 8)) % 8;
}

/** Nome da direção para cada índice de setor (ver `angleToSector8`). */
const SECTOR_NAMES: Dir8[] = ['E', 'SE', 'S', 'SW', 'W', 'NW', 'N', 'NE'];

/**
 * Vetor de GRID (dx, dy em células) correspondente a cada direção de TELA,
 * com a câmera em 0. É o inverso de `velocityToDir(..., 'iso8', 0)`:
 * `velocityToDir(DIR8_TO_GRID[d].x, DIR8_TO_GRID[d].y, 'iso8', 0, ...) === d`.
 *
 * Usado para guardar a orientação de uma entidade PARADA como vetor de grade
 * (estável) e reconvertê-la em direção de tela a cada frame — assim o NPC
 * parado também "vira" junto com a rotação da câmera, igual ao jogador.
 */
export const DIR8_TO_GRID: Record<Dir8, { x: number; y: number }> = {
  E:  { x: 1,  y: -1 },
  SE: { x: 1,  y: 0  },
  S:  { x: 1,  y: 1  },
  SW: { x: 0,  y: 1  },
  W:  { x: -1, y: 1  },
  NW: { x: -1, y: 0  },
  N:  { x: -1, y: -1 },
  NE: { x: 0,  y: -1 },
};

/**
 * Converte um vetor de GRID (dx, dy em células) para a orientação de TELA
 * isométrica e devolve o ângulo. Mesma convenção do renderer de personagens
 * do projeto: +x da grade aponta para a tela em ↘ (SE), +y da grade em ↙ (SW).
 *
 * `cameraAngle` (rad) gira o vetor junto com a câmera — assim o sprite
 * continua "encarando" a direção certa mesmo depois de girar o mapa. Passe 0
 * se a câmera não gira ou se você quer a direção travada.
 */
export function gridVectorToScreenAngle(dx: number, dy: number, cameraAngle = 0): number {
  // rotação da câmera no espaço da grade
  const cos = Math.cos(cameraAngle);
  const sin = Math.sin(cameraAngle);
  const gx = dx * cos - dy * sin;
  const gy = dx * sin + dy * cos;
  // grade → tela isométrica (mesma fórmula do isoProject, sem escala)
  const screenX = gx - gy;
  const screenY = gx + gy;
  return Math.atan2(screenY, screenX);
}

/**
 * Direção (nome) a partir de um vetor de velocidade.
 *
 * @param vx,vy       componentes do vetor. Se ~zero, retorna `fallback`.
 * @param set         'iso8' (vetor em grade), 'screen8' ou 'screen4'.
 * @param cameraAngle usado só quando `set === 'iso8'`.
 * @param fallback    direção devolvida quando o vetor é ~nulo (parado).
 */
export function velocityToDir(
  vx: number,
  vy: number,
  set: DirectionSet,
  cameraAngle: number,
  fallback: Dir8,
): Dir8 {
  if (Math.abs(vx) < 1e-6 && Math.abs(vy) < 1e-6) return fallback;

  let angle: number;
  if (set === 'iso8') {
    angle = gridVectorToScreenAngle(vx, vy, cameraAngle);
  } else {
    angle = Math.atan2(vy, vx);
  }

  const sector = angleToSector8(angle);
  const name = SECTOR_NAMES[sector];

  if (set === 'screen4') {
    // colapsa diagonais no eixo cardinal dominante
    if (name === 'SE' || name === 'SW') return 'S';
    if (name === 'NE' || name === 'NW') return 'N';
  }
  return name;
}

/**
 * Índice da LINHA do spritesheet para uma direção, respeitando `rowOrder`.
 * `rowOrder[i] === dir`  ⇒  linha `i`.
 */
export function dirToRow(dir: Dir8, rowOrder: Dir8[] = DEFAULT_ROW_ORDER): number {
  const row = rowOrder.indexOf(dir);
  return row < 0 ? 0 : row;
}

/** Frame (linha, coluna) resolvido para um instante da animação. */
export interface SpriteFrame {
  row: number;
  col: number;
  dir: Dir8;
}

/**
 * Resolve o frame do spritesheet a desenhar AGORA.
 *
 * @param anim        bloco `animation` do perfil JSON
 * @param vx,vy       vetor de velocidade da entidade neste frame
 * @param moving      a entidade está se deslocando? (false ⇒ usa `idleCol`)
 * @param elapsedSec  tempo acumulado de caminhada (para ciclar `walkCols`)
 * @param cameraAngle ângulo da câmera (rad) — só afeta `directionSet: 'iso8'`
 * @param lastDir     última direção conhecida (mantida enquanto parado)
 * @param idleFacingVec vetor de GRID que a entidade encara quando PARADA; se
 *                    informado (e `set === 'iso8'`), a direção parada também
 *                    gira com a câmera. Se ausente, usa `lastDir` fixo.
 */
export function resolveSpriteFrame(
  anim: AnimationConfig,
  vx: number,
  vy: number,
  moving: boolean,
  elapsedSec: number,
  cameraAngle: number,
  lastDir: Dir8,
  idleFacingVec?: { x: number; y: number },
): SpriteFrame {
  const set = anim.directionSet ?? 'iso8';
  const rowOrder = anim.rowOrder ?? DEFAULT_ROW_ORDER;
  const idleCol = anim.idleCol ?? 0;
  const walkCols = anim.walkCols?.length ? anim.walkCols : [1, 2];
  const walkFps = anim.walkFps ?? 8;

  const dir = moving
    ? velocityToDir(vx, vy, set, cameraAngle, lastDir)
    : (set === 'iso8' && idleFacingVec && (idleFacingVec.x !== 0 || idleFacingVec.y !== 0)
        ? velocityToDir(idleFacingVec.x, idleFacingVec.y, 'iso8', cameraAngle, lastDir)
        : lastDir);

  const row = Math.min(dirToRow(dir, rowOrder), Math.max(0, anim.rows - 1));

  let col = idleCol;
  if (moving && walkCols.length > 0 && anim.cols > 1) {
    const step = Math.floor(elapsedSec * walkFps) % walkCols.length;
    col = Math.min(walkCols[step], anim.cols - 1);
  }

  return { row, col, dir };
}
